import { useEffect, useRef } from 'react'
import { getPreferenceStorageKey, SYSTEM_DEFAULTS } from '@airclou/utils'
import { useUserPreferences } from './UserPreferencesProvider'

/**
 * The bit of an i18next instance this needs. Structural, so `packages/shell`
 * takes no dependency on i18next itself and each app passes its own instance.
 */
export interface LanguageSyncI18n {
  language: string
  /**
   * Typed as returning a promise because i18next's does — `Promise<TFunction>`.
   * Declaring it `unknown` would let a caller quietly drop a rejection: today
   * nothing can reject, since both apps bundle their locales, but an app that
   * adds `i18next-http-backend` to load them lazily gets a `changeLanguage` that
   * rejects on a failed fetch. Keeping the promise in the type forces this
   * component to decide what a failure means rather than swallowing it.
   */
  changeLanguage: (language: string) => Promise<unknown>
}

export interface LanguageSyncProps {
  /** The app's i18next instance. */
  i18n: LanguageSyncI18n
}

/**
 * Pushes the language preference into i18next, so the selector in Account >
 * Preferences actually changes the UI. Render inside `UserPreferencesProvider`.
 *
 * Shared rather than copied because the rule has two non-obvious properties, and
 * both were learned from bugs:
 *
 * 1. **It only syncs once the user has actually CHOSEN a language.**
 *    `preferences.language` falls back to SYSTEM_DEFAULTS ('en') when nothing is
 *    stored, while the apps run i18next's LanguageDetector over
 *    `['localStorage', 'navigator', 'htmlTag']`. Syncing unconditionally sees a
 *    browser-detected `zh`, decides it disagrees with the 'en' default, and
 *    forces every Chinese-locale visitor who never touched the setting into
 *    English. That shipped, and was live in helpdesk.
 *
 *    "Chosen" is two signals, not one, and using only the first was itself a
 *    bug. The stored key covers a choice made in this browser. But helpdesk
 *    passes a server-side `appOverrides` row, and the provider does NOT seed
 *    localStorage from it — so on a second device the preference is real and the
 *    key is absent, and a key-only guard would ignore a language the user did
 *    choose. A resolved value that differs from the system default can only have
 *    come from an explicit source, so it counts too.
 *
 *    One case is still not distinguishable and is accepted: an explicit choice
 *    of *English* that reaches this browser only through the server, on a
 *    non-English browser. It looks identical to never having chosen. Closing it
 *    needs the provider to report each preference's source; until then this is
 *    the rarer and milder of the two failures.
 *
 * 2. **No dependency array, on purpose.** Half the decision lives in
 *    localStorage and is not reactive. Keying on `preferences.language` alone
 *    strands the likeliest first use: the UI is Chinese while the select already
 *    reads "English", so choosing English stores the key without moving the
 *    resolved value, nothing re-runs, and the UI stays Chinese until a reload.
 *    This component re-renders exactly when the provider's context object
 *    changes — every `setPreference` — and the body is two reads and a
 *    comparison that only calls out when they differ.
 *
 * NOTE on `globalDefaults`: the provider no longer seeds the localStorage
 * key from it as a *choice*. An account value for language is written to
 * localStorage only as a marked copy of the account, and the provider's
 * `isChosen` — which this component asks — answers from the account layer and
 * the session's own changes, not from the mere presence of the key. So the
 * first signal keeps meaning "chose". No app passes `globalDefaults` today.
 *
 * NOT HEALED, on purpose: anyone already forced into English by the unguarded
 * version still has it. i18next's detector caches with `caches: ['localStorage']`
 * under `{app}:i18nextLng`, so that forced `changeLanguage('en')` was written to
 * disk and is read first on every later load — this guard never revisits it, and
 * the fix is invisible to exactly the people it was written for. They are
 * distinguishable: a forced user has `{app}:i18nextLng` set and NO
 * `{app}:language`, because the forced switch never went through
 * `setPreference`, whereas someone who genuinely chose English has both. So a
 * one-time repair is possible — clear the cached detector value when the
 * preference key is absent — but it mutates state that already exists on real
 * users' machines, which is a call for a human to make, not a side effect of a
 * bug fix. Raised, not taken.
 */
/**
 * Has the user actually chosen a language, as opposed to falling through to the
 * system default? Two signals, because one was not enough:
 *
 *   - the stored key, which covers a choice made in this browser;
 *   - a resolved value differing from `SYSTEM_DEFAULTS`, which can only come
 *     from an explicit source — helpdesk's server-side `appOverrides` row does
 *     not seed localStorage, so on a second device the key is absent and the
 *     preference is real.
 *
 * Exported so the Preferences select and the sync agree on what "chosen" means.
 * They did not, once: the select showed the stored preference while the sync
 * deliberately left i18next alone, so a Chinese page reported "English".
 */
export function hasChosenLanguage(appSlug: string, resolvedLanguage: string): boolean {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(getPreferenceStorageKey(appSlug, 'language'))
  } catch {
    // localStorage unavailable.
  }
  return Boolean(stored) || resolvedLanguage !== SYSTEM_DEFAULTS.language
}

/**
 * Which language the Preferences select should display.
 *
 * Not simply `preferences.language`: with nothing chosen that resolves to the
 * 'en' default while i18next may have detected the browser's `zh`, and since
 * `LanguageSync` deliberately no longer forces those into agreement, the control
 * would read "English" on a page rendered entirely in Chinese.
 *
 * Nor simply the detected language: right after a selection, `preferences`
 * updates synchronously while `i18n.language` only advances once
 * `changeLanguage` resolves, so preferring detection makes the control snap back
 * to the old value for a render. An explicit choice therefore wins; detection
 * only fills the gap when there is no choice to show.
 *
 * `supported` is the list the select actually offers, so the returned value
 * always has a matching option.
 */
export function resolveDisplayLanguage({
  i18n,
  appSlug,
  preferenceLanguage,
  supported,
}: {
  i18n: Pick<LanguageSyncI18n, 'language'> & { resolvedLanguage?: string }
  appSlug: string
  preferenceLanguage: string
  supported: readonly string[]
}): string {
  if (hasChosenLanguage(appSlug, preferenceLanguage)) return preferenceLanguage
  const detected = (i18n.resolvedLanguage || i18n.language || '').split('-')[0]
  return supported.includes(detected) ? detected : preferenceLanguage
}

export function LanguageSync({ i18n }: LanguageSyncProps) {
  // The slug comes from the provider rather than a second prop: it was written
  // three times per app, and one disagreement would point the sync at a
  // different localStorage namespace than the preferences it is syncing.
  const { preferences, appSlug, isChosen } = useUserPreferences()
  // A third signal, for the session only. With localStorage unavailable —
  // Safari private mode, blocked site data — `setPreference`'s write throws and
  // is swallowed, so the stored key never appears; choosing the DEFAULT language
  // then satisfies neither of the other two signals and the control does
  // nothing at all. Remembering that the value moved while this component was
  // mounted covers it without persisting anything.
  const seenLanguage = useRef<string | null>(null)
  // `i18n.language` only advances once `changeLanguage` resolves, and this
  // effect runs on every render by design — so without this a re-render during
  // a switch fires a second one. Harmless with bundled locales; with
  // `i18next-http-backend`, which the type doc above explicitly anticipates,
  // each duplicate is another locale fetch and another rejection to log.
  const inFlight = useRef<string | null>(null)
  // The most recently requested language, so a switch that resolves late can
  // check whether it is still the one wanted.
  const latest = useRef<string | null>(null)

  useEffect(() => {
    let chosen: string | null = null
    try {
      chosen = localStorage.getItem(getPreferenceStorageKey(appSlug, 'language'))
    } catch {
      // localStorage unavailable — leave i18next's own detection alone.
    }
    const movedThisSession =
      seenLanguage.current !== null && seenLanguage.current !== preferences.language
    if (movedThisSession) seenLanguage.current = preferences.language
    else if (seenLanguage.current === null) seenLanguage.current = preferences.language

    // A move is a choice only if someone holds the new value — this browser,
    // the account, or the session that just set it. The provider knows; ask
    // it. The other way a value moves is the account layer being *cleared* at
    // sign-out: language falls back to the default, and reading that as
    // "chose English" made i18next switch and cache 'en' for whoever signs in
    // next on this browser. (`isChosen` is optional here only so older test
    // doubles of the context keep working.)
    const heldBySomeone = isChosen ? isChosen('language') : true
    const explicitlyChosen =
      Boolean(chosen) ||
      preferences.language !== SYSTEM_DEFAULTS.language ||
      (movedThisSession && heldBySomeone)
    if (!explicitlyChosen) {
      // The value moved to the default because nobody holds one any more —
      // the account layer was cleared at sign-out. Not switching to 'en' is
      // right (that would cache a choice nobody made), but leaving i18next
      // where it was is not: it keeps the previous person's language, and the
      // detector has cached it under `{app}:i18nextLng`, so the next person to
      // sign in with no language chosen renders in it and keeps doing so on
      // reload. Drop the cache and go back to what the browser itself says.
      if (movedThisSession && !heldBySomeone) {
        try {
          localStorage.removeItem(`${appSlug}:i18nextLng`)
        } catch {
          // localStorage unavailable.
        }
        const detected =
          (typeof navigator !== 'undefined' && navigator.language ? navigator.language : '')
            .split('-')[0] || SYSTEM_DEFAULTS.language
        if (i18n.language !== detected) {
          Promise.resolve(i18n.changeLanguage(detected)).catch((err) => {
            console.error('[LanguageSync] failed to switch language', err)
          })
        }
      }
      return
    }
    latest.current = preferences.language
    if (i18n.language !== preferences.language && inFlight.current !== preferences.language) {
      const target = preferences.language
      inFlight.current = target
      // A rejection here means the locale could not be loaded, which the user
      // already sees: the UI stays in the previous language. Log it so the
      // cause is recoverable, rather than leaving an unhandled rejection.
      Promise.resolve(i18n.changeLanguage(preferences.language))
        .catch((err) => {
          console.error('[LanguageSync] failed to switch language', err)
        })
        .finally(() => {
          // Cleared either way: a failure must not wedge the target forever, or
          // a later retry of the same language would be ignored.
          if (inFlight.current === target) inFlight.current = null
          // Re-check against the CURRENT preference, not the one this call was
          // for. Choosing zh and then en before zh resolves leaves i18next on
          // Chinese while the preference and the select say English — and
          // nothing re-renders this component afterwards, so the mismatch would
          // persist until the next preference change or a reload.
          if (latest.current !== null && i18n.language !== latest.current) {
            Promise.resolve(i18n.changeLanguage(latest.current)).catch((err) => {
              console.error('[LanguageSync] failed to switch language', err)
            })
          }
        })
    }
  })

  return null
}
